import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Bank } from '@core/model/domain/bank';
import { GetBanks } from '@core/usecase/getBanks';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { filterBanks } from './bank-search';
import { BankChoiceVM } from './bank-choice.viewmodel';
import { BankComponent } from './components/bank.component';
import { SearchBarComponent } from '../../../../shared/components/search-bar/search-bar.component';

const SEARCH_TRACKING_DEBOUNCE_MS = 2000;

@Component({
  selector: 'app-bank-choice',
  templateUrl: './bank-choice.component.html',
  standalone: true,
  imports: [FormsModule, TranslateModule, BankComponent, SearchBarComponent],
})
export class BankChoiceComponent implements OnInit, OnDestroy {
  private banks: Bank[] = [];
  private readonly searchSubject = new Subject<string>();
  private searchTrackingSubscription?: Subscription;

  topBanks: Bank[] = [];
  otherBanks: Bank[] = [];
  bankChoiceVM: BankChoiceVM;
  searchTerm: string = '';
  isSearchBarFocused: boolean = false;

  constructor(
    private getBanks: GetBanks,
    private tracker: TrackerService,
    private storeService: StoreService
  ) {
    this.bankChoiceVM = new BankChoiceVM(this.getBanks, this.tracker, this.storeService);
  }

  async ngOnInit(): Promise<void> {
    const banks = await this.bankChoiceVM.onInit();
    this.setBanks(banks);

    this.searchTrackingSubscription = this.searchSubject
      .pipe(debounceTime(SEARCH_TRACKING_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe((term) => {
        this.bankChoiceVM.trackSearch(term, this.filteredBanks.length);
      });
  }

  ngOnDestroy(): void {
    this.searchTrackingSubscription?.unsubscribe();
    this.searchSubject.complete();
  }

  setBanks(banks: Bank[]) {
    this.banks = banks;
    const { topBanks, otherBanks } = this.bankChoiceVM.getTopBanksAndOtherBanks(this.banks);
    this.topBanks = topBanks;
    this.otherBanks = otherBanks;
  }

  isTestBankConnector(bankUid: string): boolean {
    return bankUid === environment.testBankConnector && environment.env !== 'prod';
  }

  searchTermChanged(value: string) {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  get filteredBanks(): Bank[] {
    return filterBanks(this.banks, this.searchTerm);
  }

  get hasSearchTerm(): boolean {
    return this.searchTerm !== undefined && this.searchTerm.trim() !== '';
  }
}
